import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { StaggerGroup } from '@/shared/ui/animations';
import type { Analysis } from '@/entities/analysis';
import {
  AnalysisRailToggle,
  AnalysisSidebar,
  DataSourcesPanel,
  useHasActivatedDataSources,
} from '@/features/analysis';

import { AnalysisPageHeader, ANALYSIS_PAGE_HEADER_STAGGER_SLOTS } from './components';
import { SourcePanel, SourcePanelProvider, type Source } from './components/SourcePanel';

interface AnalysisResultContentProps {
  analysis: Analysis;
}

export const AnalysisResultContent = ({ analysis }: AnalysisResultContentProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const { t } = useTranslation('analysis');

  const [navOpen, setNavOpen] = useState(true);
  const [dataSourcesOpen, setDataSourcesOpen] = useState(false);
  const hasDataSources = useHasActivatedDataSources();
  const [source, setSource] = useState<Source | null>(null);

  // The citation that opened the panel, so closing can hand focus back to it. The panel goes
  // `inert` while still mounted, which would otherwise drop focus to the body, and the citation
  // sits mid-report with the panel after it in DOM order — there is no tabbing back.
  const trigger = useRef<HTMLElement | null>(null);

  const openSource = useCallback((next: Source, from?: HTMLElement | null) => {
    trigger.current = from ?? null;
    setSource(next);
  }, []);

  const closeSource = useCallback(() => {
    setSource(null);
    trigger.current?.focus();
    trigger.current = null;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.scrollLeft = 0;

    // When the section changed because the user picked an item in the rail, move focus into the
    // content so the next Tab lands inside the section instead of walking the rest of the rail.
    // Gated on focus being in the rail so initial load, URL navigation and the citation panel
    // (which restores focus itself) keep theirs.
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('[data-testid="analysis-sidebar-v2"]')) {
      el.focus();
    }
  }, [pathname]);

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden bg-surface p-2 dark:bg-theme-background">
      <AnalysisPageHeader
        analysis={analysis}
        onOpenDataSources={hasDataSources ? () => setDataSourcesOpen(true) : undefined}
      />

      <div className="flex min-h-0 flex-1 overflow-hidden gap-2 rounded-md">
        <div className="flex flex-row bg-card rounded-lg w-full dark:bg-background">
          <AnalysisSidebar
            analysisId={analysis.id}
            open={navOpen}
            onClose={() => setNavOpen(false)}
          />

          <div
            ref={scrollRef}
            data-testid="analysis-scroll"
            // `tabIndex={-1}` so a rail selection can hand focus here; it stays out of the tab
            // order, and `outline-none` keeps the container itself from drawing a focus ring.
            tabIndex={-1}
            className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-auto px-6 pb-12 pt-5 outline-none"
          >
            {/* Sticky so the rail can be reopened from anywhere in a long report; the fill
                keeps the ghost button legible over the content scrolling beneath it. */}
            {!navOpen && (
              <div className="sticky top-0 z-10 w-fit rounded-md bg-card">
                <AnalysisRailToggle
                  label={t('analysis.analysisSidebar.viewNavigation')}
                  onClick={() => setNavOpen(true)}
                />
              </div>
            )}

            {/* Fills the scroll area so a section's layout is at least viewport-tall and an
                empty state can centre in it rather than sitting under the header. */}
            <div className="flex justify-center mx-auto w-full flex-1">
              <StaggerGroup startIndex={ANALYSIS_PAGE_HEADER_STAGGER_SLOTS}>
                <SourcePanelProvider onOpen={openSource}>
                  <Outlet context={{ analysis }} />
                </SourcePanelProvider>
              </StaggerGroup>
            </div>
          </div>
        </div>
        <SourcePanel source={source} onClose={closeSource} />

        <DataSourcesPanel
          analysis={analysis}
          open={dataSourcesOpen}
          onClose={() => setDataSourcesOpen(false)}
        />
      </div>
    </div>
  );
};
